import { FormControl, Select, MenuItem, Typography } from "@mui/material";
import { useMemo } from "react";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useFiltersWithContext } from "../filter-context/useFiltersWithContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { LabeledFilter } from "../createFilterStore";

type OptionBasedDropdownFilterProps = {
  labelKey: string;
  options: { name: string; label: string }[];
};

export const OptionBasedDropdownFilter = (
  props: OptionBasedDropdownFilterProps,
) => {
  const { labelKey, options } = props;

  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const { getFiltersWithName, setFilter, deleteFilter } =
    useFiltersWithContext();
  const { translate } = useTranslation();

  const selectedFilters = useMemo(() => {
    return options.reduce<LabeledFilter[]>((acc, option) => {
      const filter = getFiltersWithName(option.name);
      if (!filter || filter.length === 0) return acc;
      return [...acc, filter[0][1]];
    }, []);
  }, [options, getFiltersWithName]);

  const handleSelect = (name: string) => {
    const optionLabel =
      options.find((o) => o.name === name)?.label ??
      "filters.placeholders.none";

    if (selectedFilters && selectedFilters.length > 0) {
      deleteFilter(selectedFilters[0].field, selectedFilters[0].operator);
    }

    setFilter({
      field: name,
      value: "true",
      labelInfo: {
        descriptionKey: optionLabel,
        value: undefined,
      },
      operator: "eq",
    });
  };

  const handleClear = (filterName: string) => {
    deleteFilter(filterName, "eq");
  };

  return (
    <FormControl
      fullWidth
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Typography flex={1}>
        {translate(labelKey, { market: selectedCountry?.countryName })}
      </Typography>
      <Select
        size="small"
        sx={{ minWidth: 200 }}
        value={
          selectedFilters.length > 0 ? selectedFilters[0].field : "default"
        }
        onChange={(e) => {
          const name = e.target.value;
          return name != "default"
            ? handleSelect(name)
            : handleClear(selectedFilters[0].field);
        }}
      >
        <MenuItem value="default">
          <em>{translate("filters.placeholders.none")}</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.name} value={option.name}>
            {translate(option.label, { market: selectedCountry?.countryName })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
